import { yupResolver } from '@hookform/resolvers/yup';
import {
  Backdrop,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
} from '@mui/material';
import { capitalCase } from 'capital-case';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  AppointmentWrapper,
  WrappedAppointment,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Appointment';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import DateTimePicker from 'src/components/DateTimePicker/DateTimePicker';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import useComponentsConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useComponentsConfigData';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import { getReferenceResourceId } from 'src/careflow/helpers/common';
import { isTimeIncludedInDate } from 'src/utils/dates';
import * as Yup from 'yup';
import AppointmentParticipantList from './AppointmentParticipantList';
import { ReasonAppointmentForm } from './ReasonAppointmentForm';
import { useAppointments } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { useSnackbar } from 'notistack';
import CancelButton from 'src/careflow/common/CancelButton';
import DialogCloseButton, { useUnsavedCloseGuard } from 'src/components/DialogCloseButton';

export enum AppointmentStatus {
  PROPOSED = 'proposed',
  PENDING = 'pending',
  BOOKED = 'booked',
  ARRIVED = 'arrived',
  FULFILLED = 'fulfilled',
  CANCELLED = 'cancelled',
  NO_SHOW = 'noshow',
  ENTERED_IN_ERROR = 'entered-in-error',
  CHECKED_IN = 'checked-in',
  WAITLIST = 'waitlist',
}

export enum AppointmentParticipantRequired {
  REQUIRED = 'required',
  OPTIONAL = 'optional',
  INFORMATION_ONLY = 'information-only',
}

export enum AppointmentParticipantStatus {
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  TENTATIVE = 'tentative',
  NEEDS_ACTION = 'needs-action',
}

type CustomParticipant = {
  actor: {
    label: string;
    value: string;
  };
  status: string;
  required: string;
};

type FormValue = {
  status: string;
  start: string;
  end: string;
  description: string;
  minutesDuration: number;
  participant: CustomParticipant[];
  reasonCode: string;
  reasonText: string;
};

type Props = {
  patient: WrappedPatient | null;
  appointment: WrappedAppointment | null;
  appointmentId?: string | null;
  open: boolean;
  onClose: VoidFunction;
  handleAppointment: any;
};

export function AppointmentForm({
  patient,
  appointment,
  appointmentId,
  open,
  onClose,
  handleAppointment,
}: Props) {
  const { i18n } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const componentsConfigData = useComponentsConfigData();
  const [appointmentResource, setAppointmentResource] = useState<WrappedAppointment | null>(null);

  const formConfig = useMemo(() => {
    if (!componentsConfigData) {
      return;
    }

    // CHECK HERE the context of the form if needed to switch the config data

    return componentsConfigData.appointmentFormConfig;
  }, [componentsConfigData]);

  const [
    appointmentFromHook,
    { create: createAppointment, update: updateAppointment, isFetching: isFetchingAppointment },
  ] = useAppointments({
    filter: { _id: appointmentId },
    autofetch: !!appointmentId,
    map: AppointmentWrapper,
  });

  useEffect(() => {
    if (!open) return;
    const setAppointment = appointmentFromHook?.[0] || appointment;
    if (setAppointment) {
      setAppointmentResource(setAppointment);
      setIsTimeIncludedInEndInitially(isTimeIncludedInDate(setAppointment?.end ?? null));
      setIsTimeIncludedInStartInitially(isTimeIncludedInDate(setAppointment?.start ?? null));
    }
  }, [open, appointmentFromHook, appointment]);

  useEffect(() => {
    if (appointmentResource) {
      reset(defaultValues);
    }
  }, [appointmentResource]);

  const {
    valueSets: [appointmentReasons],
    isLoading: isLoadingValuesets,
  } = useValueSetsByIdentifiers([formConfig?.reason?.valueSetIdentifier || 'encounter-reason']);

  const [isTimeIncluded, setIsTimeIncluded] = useState(true);

  const [isTimeIncludedInEndInitially, setIsTimeIncludedInEndInitially] = useState(false);
  const [isTimeIncludedInStartInitially, setIsTimeIncludedInStartInitially] = useState(false);

  const EventSchema = Yup.object().shape({
    status: Yup.string().required('Status is required'),
    reasonCode: Yup.string().required('Reason is required'),
    reasonText: Yup.string().required('Reason is required'),
    start: Yup.date()
      .required('Start is required')
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null)),
    end: Yup.date()
      .required('End is required')
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null))
      .test('is-greater', 'End date must be greater than start date', function (value) {
        const startDate = moment(this.parent.start);
        const endDate = moment(value);

        return startDate.isSameOrBefore(
          endDate,
          isTimeIncludedInDate(endDate) && isTimeIncludedInDate(startDate) ? undefined : 'date'
        );
      }),
    description: Yup.string().required('Description is required'),
    participant: Yup.array(
      Yup.object({
        actor: Yup.object({
          label: Yup.string(),
          value: Yup.string().nullable().required('Participant is required'),
        }).required('Participant is required'),
        status: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .required('Status is required'),
        required: Yup.string()
          .nullable()
          .transform((value) => (value === '' ? null : value))
          .required('Required is required'),
      })
    ),
  });

  const defaultValues = {
    status: appointmentResource?.status || '',
    start: appointmentResource?.start || null,
    end: appointmentResource?.end || null,
    description: appointmentResource?.description || '',
    minutesDuration: appointmentResource?.minutesDuration || null,
    participant:
      appointmentResource?.participant
        ?.filter((x1) => getReferenceResourceId(x1.actor?.reference ?? '') !== patient?.id)
        .map((x2) => ({
          ...x2,
          actor: {
            label: x2.actor?.display,
            value: x2.actor?.reference && getReferenceResourceId(x2.actor?.reference),
          },
        })) || [],
    reasonCode:
      appointmentResource?.reasonCode?.[0]?.coding?.[0].code ||
      formConfig?.reason?.defaultValueCode ||
      null,
    reasonText: appointmentResource?.reasonCode?.[0]?.text || '',
  } as FormValue;

  const methods = useForm<FormValue>({ resolver: yupResolver(EventSchema), defaultValues });

  const { reset, control, handleSubmit } = methods;

  const mapAppointment = (dataForm: FormValue) => {
    const setParticipant =
      dataForm?.participant?.map((x1) => ({
        ...x1,
        actor: {
          reference: `Practitioner/${x1.actor?.value}`,
          display: x1.actor?.label,
        },
      })) ?? [];

    const mapParticipant: any = [
      ...setParticipant,
      {
        actor: {
          reference: `${patient?.resourceType}/${patient?.id}`,
          display: patient?.getFullName(),
        },
        required: 'required',
        status: 'accepted',
      },
    ];

    let reasonCoding = { code: dataForm.reasonCode, display: '-' };

    if (appointmentReasons) {
      const foundOption = appointmentReasons
        ?.asList?.()
        .find((item) => item.code === dataForm.reasonCode);
      if (foundOption?.display) {
        reasonCoding.display = foundOption?.display;
      }
    }

    let reasonCodeableConcept = {
      ...(reasonCoding?.code && { coding: [reasonCoding] }),
      text: '',
    };

    if (dataForm.reasonText) {
      reasonCodeableConcept.text = dataForm.reasonText;
    }

    const reasonCode = [reasonCodeableConcept];

    return {
      ...dataForm,
      reasonText: undefined,
      reasonCode,
      minutesDuration: undefined,
      resourceType: 'Appointment',
      ...(appointmentResource !== null && { id: appointmentResource?.id }),
      start: dataForm?.start && moment(dataForm?.start).toISOString(),
      end: dataForm?.end && moment(dataForm?.end).toISOString(),
      participant: mapParticipant,
      ...(appointment === null && { created: new Date().toISOString() }),
    };
  };

  const onSubmit = async (dataForm: FormValue) => {
    // if data is not arriving here when trying to log it
    // try disabling validations and check again to look for the problem
    const resultMap: any = mapAppointment(dataForm);
    let result: any;
    console.debug('resultMap', resultMap);
    try {
      if (appointmentResource?.id) {
        result = await updateAppointment(resultMap);
      } else {
        result = await createAppointment(resultMap);
      }

      enqueueSnackbar(
        appointmentResource?.id ? 'Appointment was updated' : 'Appointment was created'
      );
      const resultToHandle = Array.isArray(result) ? result[0] ?? null : result;
      handleAppointment(resultToHandle);
    } catch (error) {
      enqueueSnackbar('An occurred an error', { variant: 'error' });
    }
    handleClose();
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const requestClose = useUnsavedCloseGuard(handleClose, methods.formState.isDirty);

  const calculateDurationFromStartEnd = (start, end) => {
    if (start && end) {
      const startMoment = moment(start);
      const endMoment = moment(end);
      const duration = endMoment.diff(startMoment, 'minutes');
      return duration;
    }
    return 0;
  };
  return (
    <Dialog onClose={() => void requestClose()} open={open} fullWidth={true} maxWidth="md">
      <DialogCloseButton
        onClose={handleClose}
        hasUnsavedChanges={methods.formState.isDirty}
        disabled={isFetchingAppointment}
      />
      <Backdrop
        open={isFetchingAppointment}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DialogTitle>
        {appointmentResource
          ? `${i18n('patients.details.appointments.titleEditAppointment', 'crs')}`
          : `${i18n('patients.details.appointments.titleAddAppointment', 'crs')}`}{' '}
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <RHFSelect
                  name="status"
                  label={`${i18n('patients.details.appointments.status', 'crs')}*`}
                >
                  <MenuItem disabled></MenuItem>
                  {Object.entries(AppointmentStatus).map((option) => (
                    <MenuItem key={option[0]} value={option[1]}>
                      {capitalCase(option[1])}
                    </MenuItem>
                  ))}
                </RHFSelect>

                <Grid item xs={12} hidden={formConfig?.reason?.hide}>
                  <ReasonAppointmentForm
                    optionList={appointmentReasons?.asList?.() ?? []}
                    isLoading={isLoadingValuesets}
                    defaultValue={formConfig?.reason?.defaultValueCode}
                  />
                </Grid>

                <Grid item xs={12} hidden={formConfig?.reasonText?.hide}>
                  <RHFTextField
                    name="reasonText"
                    label={`${i18n('patients.details.appointments.reasonText', 'crs')}*`}
                    multiline
                    rows={4}
                  />
                </Grid>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: 1 }}>
                  <Controller
                    name="start"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <DateTimePicker
                          {...field}
                          isTimeIncluded={
                            appointment ? isTimeIncludedInStartInitially : isTimeIncluded
                          }
                          isTimeOptional
                          onTimeInclusionChange={setIsTimeIncluded}
                          textFieldProps={{
                            fullWidth: true,
                            placeholder: i18n(
                              'patients.details.appointments.placeholderStartDate',
                              'crs'
                            ),
                          }}
                          label={`${i18n('patients.details.appointments.start', 'crs')}*`}
                          errorMessage={error?.message}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="end"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <DateTimePicker
                          {...field}
                          textFieldProps={{
                            fullWidth: true,
                            placeholder: i18n(
                              'patients.details.appointments.placeholderEndDate',
                              'crs'
                            ),
                          }}
                          label={`${i18n('patients.details.appointments.end', 'crs')}*`}
                          isTimeIncluded={
                            appointment ? isTimeIncludedInEndInitially : isTimeIncluded
                          }
                          isTimeOptional
                          onChange={(date) => {
                            field.onChange(date);
                            const startValue = methods.getValues('start');
                            const endValue = date; // Nuevo valor del campo 'end'

                            if (startValue && endValue) {
                              const calculatedDuration = calculateDurationFromStartEnd(
                                startValue,
                                endValue
                              );
                              methods.setValue('minutesDuration', calculatedDuration);
                            }
                          }}
                          errorMessage={error?.message}
                        />
                      </>
                    )}
                  />
                </Stack>
                <AppointmentParticipantList />
                <RHFTextField
                  name="description"
                  label={`${i18n('patients.details.appointments.description', 'crs')}*`}
                  multiline
                  rows={4}
                />
              </Stack>
            </Grid>
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={() => void requestClose()}>{i18n('cancel')}</CancelButton>

              <Button variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
